import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/joy";
import { useColorScheme } from "@mui/joy/styles";
import { useOrgTheme } from "../providers/OrgThemeProvider";

import XMachineLight from "../assets/logos/metalmind/x-machine-light.png";
import XMachineDark from "../assets/logos/metalmind/x-machine-dark.png";
import SSILight from "../assets/logos/ssi/logo-light.png";
import SSIDark from "../assets/logos/ssi/logo-dark.png";
import { getThemeName } from "../config/runtimeConfig";

interface ThemeLogoProps {
  alt?: string;
  sx?: object;
}

const ThemeLogo: React.FC<ThemeLogoProps> = ({ alt, sx = {} }) => {
  const { mode } = useColorScheme();
  const { logoLightUrl, logoDarkUrl, brandingAssetVersion } = useOrgTheme();
  const [imageFailed, setImageFailed] = useState(false);
  const themeName = getThemeName();
  const isDark = mode === "dark";

  useEffect(() => {
    setImageFailed(false);
  }, [logoLightUrl, logoDarkUrl, brandingAssetVersion, mode]);

  const staticLogoSrc = useMemo(() => {
    if (themeName === "ssi") return isDark ? SSIDark : SSILight;
    return isDark ? XMachineDark : XMachineLight;
  }, [isDark, themeName]);

  const orgLogoSrc = useMemo(() => {
    const rawSrc = isDark ? logoDarkUrl : logoLightUrl;
    if (!rawSrc) return null;
    const separator = rawSrc.includes("?") ? "&" : "?";
    return `${rawSrc}${separator}v=${brandingAssetVersion}`;
  }, [brandingAssetVersion, isDark, logoDarkUrl, logoLightUrl]);

  const logoSrc = !imageFailed && orgLogoSrc ? orgLogoSrc : staticLogoSrc;

  const getLogoWidth = () => {
    if (!imageFailed && orgLogoSrc) return "auto";
    if (themeName === "ssi") return isDark ? "75px" : "100px";
    return "120px";
  };

  const getAltText = () => {
    if (alt) return alt;
    if (themeName === "ssi") return "SSI Strategy Logo";
    return "X-Machine Logo";
  };

  return (
    <Box
      component="img"
      src={logoSrc}
      alt={getAltText()}
      onError={() => setImageFailed(true)}
      sx={{
        width: getLogoWidth(),
        height: "auto",
        maxHeight: "50px",
        cursor: "pointer",
        transition: "opacity 0.2s ease-in-out",
        display: "block",
        alignSelf: "flex-start",
        "&:hover": { opacity: 0.8 },
        ...sx,
      }}
    />
  );
};

export default ThemeLogo;
